import type { NxCalendarDay } from './types';

/** One cell of a month raster. `inMonth` is false for the adjacent-month fillers. */
export type MonthCell = {
  year: number;
  /** 1-12, not the `Date` month index. */
  month: number;
  day: number;
  iso: string;
  inMonth: boolean;
};

/** Exported so nobody has to write the bare 7 again — key steps, row maths, template. */
export const DAYS_PER_WEEK = 7;
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

const pad = (value: number, width = 2) => String(value).padStart(width, '0');

export const toIso = (year: number, month: number, day: number): string =>
  `${pad(year, 4)}-${pad(month)}-${pad(day)}`;

/**
 * ISO `YYYY-MM-DD` → its parts. One parser for the whole component: the same
 * `split('-').map(Number)` used to sit in three places, each free to disagree
 * about what a malformed value means.
 */
export function parseIso(iso: string): { year: number; month: number; day: number } {
  const [year, month, day] = iso.split('-').map(Number);
  return { year, month, day };
}

/**
 * True for an ISO `YYYY-MM-DD` that is also a real date. The shape alone is not
 * enough: `2026-13-45` matches it, and the Date constructor would roll that
 * quietly into February 2027 — a calendar showing a month nobody asked for.
 */
export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const { year, month, day } = parseIso(value);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/** Index of `date` within its week: 0 = Monday when `firstDayOfWeek` is 1. */
export function weekdayIndex(date: Date, firstDayOfWeek: 1 | 7): number {
  return firstDayOfWeek === 1 ? (date.getDay() + 6) % 7 : date.getDay();
}

/**
 * Rows the month occupies: four (a 28-day February starting on the week's first
 * day) to six. The raster is deliberately NOT padded to a fixed six rows — an
 * empty trailing week draws a bordered box around nothing.
 */
export function monthRowCount(year: number, month: number, firstDayOfWeek: 1 | 7): number {
  const offset = weekdayIndex(new Date(year, month - 1, 1), firstDayOfWeek);
  // Day 0 of the following month is the last day of this one.
  const daysInMonth = new Date(year, month, 0).getDate();
  return Math.ceil((offset + daysInMonth) / DAYS_PER_WEEK);
}

export function buildMonthCells(year: number, month: number, firstDayOfWeek: 1 | 7): MonthCell[] {
  const offset = weekdayIndex(new Date(year, month - 1, 1), firstDayOfWeek);
  const length = monthRowCount(year, month, firstDayOfWeek) * DAYS_PER_WEEK;
  return Array.from({ length }, (_, index) => {
    // The Date constructor normalises out-of-range day numbers, so walking the
    // raster needs no millisecond arithmetic — that would drift across a DST
    // transition and silently shift every following day by one.
    const date = new Date(year, month - 1, 1 - offset + index);
    const cellMonth = date.getMonth() + 1;
    const cellYear = date.getFullYear();
    return {
      year: cellYear,
      month: cellMonth,
      day: date.getDate(),
      iso: toIso(cellYear, cellMonth, date.getDate()),
      inMonth: cellMonth === month && cellYear === year,
    };
  });
}

/**
 * ISO 8601 week number: the week holding the year's first Thursday is week 1,
 * weeks always start on Monday. Independent of the grid's `firstDayOfWeek` —
 * anything else would invent a numbering nobody shares.
 */
export function isoWeekNumber(year: number, month: number, day: number): number {
  const date = new Date(year, month - 1, day);
  // Step to the Thursday of this date's ISO week; that Thursday's calendar year
  // IS the ISO week-numbering year.
  const thursday = new Date(year, month - 1, day - ((date.getDay() + 6) % 7) + 3);
  const isoYear = thursday.getFullYear();
  // 4 January always lies in ISO week 1, so its Monday starts the year.
  const jan4 = new Date(isoYear, 0, 4);
  const week1Monday = new Date(isoYear, 0, 4 - ((jan4.getDay() + 6) % 7));
  // Difference between two local midnights: rounding absorbs the ±1 h a DST
  // transition can introduce over a 168 h span.
  return Math.round((thursday.getTime() - week1Monday.getTime()) / MS_PER_WEEK) + 1;
}

/**
 * Steps whole months and clamps the day into the target month: 31 January plus
 * one month is 28 February, not 3 March. Handing the raw day to the Date
 * constructor would roll it over and skip the month the caller asked for — and
 * a year step lands on 29 February often enough to matter.
 */
export function shiftMonths(iso: string, months: number): string {
  const { year, month, day } = parseIso(iso);
  const target = new Date(year, month - 1 + months, 1);
  // Day 0 of the following month is the last day of the target month.
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return toIso(target.getFullYear(), target.getMonth() + 1, Math.min(day, lastDay));
}

/**
 * Steps whole days. The Date constructor normalises the overflow, so this
 * crosses month and year boundaries — and a DST transition — on its own.
 */
export function shiftDays(iso: string, days: number): string {
  const { year, month, day } = parseIso(iso);
  const next = new Date(year, month - 1, day + days);
  return toIso(next.getFullYear(), next.getMonth() + 1, next.getDate());
}

/** ISO date → decoration. Later entries win, so the map is deterministic. */
export function indexDays(days: NxCalendarDay[] | undefined): Map<string, NxCalendarDay> {
  const map = new Map<string, NxCalendarDay>();
  for (const day of days ?? []) map.set(day.date, day);
  return map;
}
